import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { connect, ConnectedProps } from 'react-redux';
import {
  Grid,
  makeStyles,
  createStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import {
  Footer,
  BasePageQuotation,
  HeaderWithPercentBar,
  themeMethods,
  backgroundThemes,
} from 'design-system';

// Data Layer
import { ProductConfiguration, Quotation } from 'data-layer-cr/dist/models';
import {
  defaultUnitEntity,
  isInspectionRequired,
} from 'data-layer-cr/dist/functions/quotations';

import profiles from 'src/constants/profileTypes';

// Methods
import getProgressPercent from 'src/functions/getProgressPercent';
import isThirdPartyPayment from '../../../functions/isThirdPartyPayment';

// Redux
import { RootState } from '../../../store/rootReducer';
import {
  setStepperEmission,
  setIsOnThirdPartyPayment,
} from '../../../store/steppers/actions';

// Icons
import ProfileInfoIcon from '../../Icons/ProfileInfoIcon';
import CarIcon from '../../Icons/CarInfoIcon';
import CarHistoryIcon from '../../Icons/CarHistoryIcon';
import YourPolicyIcon from '../../Icons/YourPolicyIcon';

// Components
import NaturalPersonData from './NaturalPersonData';
import LegalPersonData from './LegalPersonData';
import Summary from './Summary';
import Confirmation from './Confirmation';
import CarData from './CarData';
import NewCarData from './NewCarData';
import DigitalSign from './DigitalSign';
import EmissionErrorHandler from './EmissionErrorHandler';
import { StepperBackButton } from '../../general';

export const shouldSetEmptyBackground = (
  pagesLenght: number,
  currentPage: number,
): boolean => currentPage + 1 === pagesLenght;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
    },
    container: {
      flexGrow: 1,
    },
    imageBackground: {
      backgroundSize: '100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'bottom',
      background: `linear-gradient(359.66deg, rgba(242, 244, 243,0.4) 13.26%, rgba(255, 255, 255,0.4) 88.97%), 
        ${themeMethods.getCurrentMonthTheme(
          new Date(),
          backgroundThemes.QUOTATION_PAGE,
        )}`,
      [theme.breakpoints.down('sm')]: {
        background: 'none',
      },
    },
    emptyBackground: {
      background: 'none',
    },
    networkDialogue: {
      padding: theme.spacing(2),
    },
    errorLayoutSeparator: {
      height: theme.spacing(2),
      width: '100%',
    },
    stepperOverflowWrapper: {
      display: 'inline-block',
      width: '100%',
      [theme.breakpoints.up('md')]: {
        marginTop: theme.spacing(1),
        width: 400,
      },
    },
    stepper: {
      backgroundColor: '#fafafa',
      position: 'fixed',
      width: '100%',
      zIndex: 10,
      marginTop: -16,
      marginLeft: -16,
    },
    IconButtonGoback: {
      width: 'max-content',
    },
    footer: {
      marginTop: 'auto',
    },
  }),
);

/** Page titles to use when we don't need to capture additional information for the car */
export const vehiclePageTitles = [
  'Datos del contratante',
  'Confirmación',
  'Certificación digital',
  'Tu póliza',
];
/** Page titles to use when additional information is required for the car */
export const vehicleRcPageTitles = [
  'Datos del contratante',
  'Datos del auto',
  'Confirmación',
  'Certificación digital',
  'Tu póliza',
];
/** Page titlesto use when additional information is required for the car and is retention */
export const vehicleRcRetentionPageTitles = [
  'Datos del contratante',
  'Verificación',
  'Confirmación',
  'Certificación digital',
  'Tu póliza',
];

export const vehiclePageIcons = [
  <ProfileInfoIcon key="user-info-icon" />,
  <CarHistoryIcon key="confirmation-icon" />,
  <CarHistoryIcon key="digital-sign-icon" />,
  <YourPolicyIcon key="policy-icon" />,
];

export const vehicleRcPageIcons = [
  <ProfileInfoIcon key="user-info-icon" />,
  <CarIcon key="car-infor-icon" />,
  <CarHistoryIcon key="confirmation-icon" />,
  <CarHistoryIcon key="digital-sign-icon" />,
  <YourPolicyIcon key="policy-icon" />,
];

export const vehicleRcRetencionPageIcons = [
  <ProfileInfoIcon key="user-info-icon" />,
  <CarHistoryIcon key="car-infor-icon" />,
  <CarHistoryIcon key="confirmation-icon" />,
  <CarHistoryIcon key="digital-sign-icon" />,
  <YourPolicyIcon key="policy-icon" />,
];

/**
 * Will return an object containing an array of the pages that need to be displayed based on whether the
 * inspection was required in previous steps, alongside another array with said pages' titles
 * and also with an array of icons.
 *
 * @param inspectionRequired - Whether the inspection was required for this quotation
 * @param withInspectionArray - The pages array for the case where inspection IS required
 * @param withoutInspectionArray - The pages array for the case where inspection IT NOT required
 * @param profileType - The type of the profile: collective or individual or retencion
 */
export function getPagesArray(
  inspectionRequired: boolean,
  withInspectionArray: React.ReactNode[],
  withoutInspectionArray: React.ReactNode[],
  profileType: string,
): {
  pages: React.ReactNode[];
  pageTitles: string[];
  pageIcons: JSX.Element[];
} {
  if (inspectionRequired) {
    return {
      pages: withInspectionArray,
      pageTitles: vehiclePageTitles,
      pageIcons: vehiclePageIcons,
    };
  }

  if (profileType === profiles.RETENCION) {
    return {
      pages: withoutInspectionArray,
      pageTitles: vehicleRcRetentionPageTitles,
      pageIcons: vehicleRcRetencionPageIcons,
    };
  }

  return {
    pages: withoutInspectionArray,
    pageTitles: vehicleRcPageTitles,
    pageIcons: vehicleRcPageIcons,
  };
}

/**
 * Returns the back button or undefined, depending on the parameters.
 * The dynamic nature of the back button is required because it must disappear when there are no valid back
 * pages, or when the condition states it.
 */
function getFirstBackAction(
  canGoBackToUnitSelection: boolean,
  onGoToUnitSelection: () => void,
): (() => void) | undefined {
  return canGoBackToUnitSelection ? onGoToUnitSelection : undefined;
}

/**
 * Extracted to prevent sonarcloud from complaining.
 * Returns the appropiate car screen, depending on whether the car registered in the current quotation is
 * identified as "new".
 */
function makeCarScreen(
  isNewCar: boolean,
  goToNextPage: () => void,
): React.ReactNode {
  return isNewCar ? (
    <NewCarData data-testid="new-car-data" onSubmit={goToNextPage} />
  ) : (
    <CarData data-testid="car-data" onSubmit={goToNextPage} />
  );
}

/**
 * Extracted to prevent sonarcloud from complaining
 *
 * Encapsulates Summary component render.
 * Note that the onSubmit for this component will vary depending on the payment type.
 * If payment type is handled by a third party provider, then the onSubmit will trigger
 * the ThirdPartyPayment component instead of emission creation.
 */
function makeSummary(
  goToNextPage: () => void,
  setIsOnThirdPartyPaymentAction: (status: boolean) => void,
  isThirdPartyPaymentFlag: boolean,
): React.ReactNode {
  return (
    <Summary
      data-testid="summary"
      onSubmit={(): void => {
        setIsOnThirdPartyPaymentAction(isThirdPartyPaymentFlag);
        goToNextPage();
      }}
    />
  );
}

/**
 * Extracted to prevent sonarcloud from complaining.
 * Returns the appropiate car screen, depending on whether the car registered in the current quotation is
 * identified as "new".
 */
function makeDigitalSignPage(): React.ReactNode {
  return <DigitalSign data-testid="digital-sign-page" />;
}

function renderCustomerData(
  customerType: 'J' | 'N',
  goToNextPage: () => void,
): JSX.Element {
  if (customerType === 'N') {
    return (
      <NaturalPersonData data-testid="emission-data" onSubmit={goToNextPage} />
    );
  }
  return (
    <LegalPersonData data-testid="legal-entity-data" onSubmit={goToNextPage} />
  );
}

export const EmissionPolicyManager = ({
  quotation,
  emissionErrorCode,
  productConfiguration,
  currentStepEmission,
  paymentType,
  canGoBackToUnitSelection,
  setStepperEmissionAction,
  onGoToUnitSelection,
  setIsOnThirdPartyPaymentAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const profileType = quotation.quotationProfile.type ?? '';

  const isNewCar = useMemo(
    () => defaultUnitEntity(quotation).isNewCar,
    [quotation],
  );
  const showError = useMemo(
    () => emissionErrorCode !== '',
    [emissionErrorCode],
  );

  const goToNextPage = useCallback(() => {
    setStepperEmissionAction(currentStepEmission + 1);
  }, [currentStepEmission, setStepperEmissionAction]);

  const emissionDataPage = useMemo(
    () => renderCustomerData(quotation.customerType, goToNextPage),
    [goToNextPage, quotation.customerType],
  );
  const policySummaryScreen = useMemo(
    () =>
      makeSummary(
        goToNextPage,
        setIsOnThirdPartyPaymentAction,
        isThirdPartyPayment(paymentType),
      ),
    [setIsOnThirdPartyPaymentAction, paymentType, goToNextPage],
  );
  const confirmationScreen = useMemo(
    () => <Confirmation data-testid="confirmation" />,
    [],
  );
  const carScreen = useMemo(
    () => makeCarScreen(isNewCar, goToNextPage),
    [goToNextPage, isNewCar],
  );
  const inspectionRequired = useMemo(
    () =>
      isInspectionRequired(quotation, productConfiguration.inspectionRequired),
    [quotation, productConfiguration],
  );
  const digitalSignPage = useMemo(() => makeDigitalSignPage(), []);

  const { pages, pageTitles, pageIcons } = useMemo(() => {
    return getPagesArray(
      inspectionRequired,
      [emissionDataPage, policySummaryScreen, digitalSignPage],
      [emissionDataPage, carScreen, policySummaryScreen, digitalSignPage],
      profileType,
    );
  }, [
    emissionDataPage,
    policySummaryScreen,
    carScreen,
    inspectionRequired,
    digitalSignPage,
    profileType,
  ]);

  const pageQuantity = pages.length;

  const goToBackPage = useCallback(() => {
    const newStep = currentStepEmission - 1;
    setStepperEmissionAction(newStep);
  }, [currentStepEmission, setStepperEmissionAction]);
  const clickAtFirstStepAction = useMemo(
    () => getFirstBackAction(canGoBackToUnitSelection, onGoToUnitSelection),
    [onGoToUnitSelection, canGoBackToUnitSelection],
  );

  const backButton = useMemo(
    () => (
      <StepperBackButton
        data-testid="back-button"
        currentStep={currentStepEmission}
        maxSteps={pages.length + 1}
        onClick={goToBackPage}
        onClickAtFirstStep={clickAtFirstStepAction}
      />
    ),
    [currentStepEmission, pages.length, goToBackPage, clickAtFirstStepAction],
  );

  const onClickAtFirstStep = useCallback(() => {
    if (currentStepEmission === 0 && clickAtFirstStepAction) {
      return clickAtFirstStepAction;
    }
    return goToBackPage;
  }, [currentStepEmission, goToBackPage, clickAtFirstStepAction]);

  if (quotation.id === -1) {
    return <div />;
  }

  const currentStep = canGoBackToUnitSelection
    ? currentStepEmission + 2
    : currentStepEmission + 1;
  const totalSteps = canGoBackToUnitSelection
    ? pageQuantity + 2
    : pageQuantity + 1;

  const setEmptyBackground = shouldSetEmptyBackground(totalSteps, currentStep);

  return (
    <div className={classes.root}>
      <div
        className={clsx(
          classes.container,
          { [classes.imageBackground]: !setEmptyBackground },
          { [classes.emptyBackground]: setEmptyBackground },
        )}
      >
        <BasePageQuotation
          data-testid="main-content"
          rightActions={null}
          leftActions={!showError ? backButton : ''}
          backgroundColor="white"
          stepperItem={
            !showError && (
              <HeaderWithPercentBar
                data-testid="stepper"
                goToBackPage={onClickAtFirstStep()}
                icon={pageIcons[currentStepEmission]}
                stepperTitle={pageTitles[currentStepEmission]}
                currentProgress={getProgressPercent(currentStep, totalSteps)}
              />
            )
          }
        >
          {showError && <EmissionErrorHandler data-testid="error" />}
          {!showError && (
            <Grid container data-testid="emission-form">
              <Grid item xs={12}>
                {currentStepEmission >= pages.length
                  ? confirmationScreen
                  : pages[currentStepEmission]}
              </Grid>
            </Grid>
          )}
        </BasePageQuotation>
      </div>
      <footer data-testid="footer" className={classes.footer}>
        <Footer
          IsMobile={isMobile}
          country="Costa Rica"
          linkText="Consulte nuestros términos y condiciones"
          linkHref="https://www.assanet.cr/condiciones-generales/#seguros-generales"
        />
      </footer>
    </div>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  emissionErrorCode: string;
  productConfiguration: ProductConfiguration;
  currentStepEmission: number;
  paymentType: string;
  canGoBackToUnitSelection: boolean;
} => ({
  quotation: state.quotation.quotation,
  emissionErrorCode: state.emissionPolicy.emissionErrorCode,
  productConfiguration: state.coverageCatalog.productConfig,
  currentStepEmission: state.stepper.currentStepEmission,
  paymentType: state.emissionPolicy.paymentFields.type,
  canGoBackToUnitSelection: state.quotation.canGoBackToUnitSelection,
});

const connector = connect(mapStateToProps, {
  setStepperEmissionAction: setStepperEmission,
  setIsOnThirdPartyPaymentAction: setIsOnThirdPartyPayment,
});
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onGoToUnitSelection: () => void;
}

export default connector(EmissionPolicyManager);
